import type { RouteObject } from 'react-router';
import { SETTINGS_SECTIONS } from './permissions';

/** The settings pages, loaded once on first use (the import is cached), so the sign-in pages stay small. */
const load = () => import('./pages');

/**
 * The organization settings routes, for owners, administrators and billing administrators of a customer organization,
 * and the help desk: /settings (the organizations the person administers) and /settings/:organizationId/:section
 * (with the help desk's help-desk/new and help-desk/:number). SectionGuard shows a
 * section only to the people the overview's permissions allow; platform-api decides every request again.
 */
export function settingsRoutes(): RouteObject[] {
  return [
    { path: 'settings', lazy: async () => ({ Component: (await load()).SettingsHomePage }) },
    {
      path: 'settings/:organizationId',
      lazy: async () => ({ Component: (await load()).SettingsLayout }),
      children: [
        { index: true, lazy: async () => ({ Component: (await load()).SettingsIndexRedirect }) },
        // The help desk's new ticket and ticket pages, behind the Help desk section's own guard.
        ...(['help-desk/new', 'help-desk/:number'] as const).map((path): RouteObject => ({
          path,
          lazy: async () => {
            const { HelpDeskNewTicketPage, HelpDeskTicketPage, SectionGuard } = await load();
            const Page = path === 'help-desk/new' ? HelpDeskNewTicketPage : HelpDeskTicketPage;
            return {
              element: (
                <SectionGuard section="help-desk">
                  <Page />
                </SectionGuard>
              ),
            };
          },
        })),
        ...SETTINGS_SECTIONS.map((section): RouteObject => ({
          path: section,
          lazy: async () => {
            const { SECTION_PAGES, SectionGuard } = await load();
            const Page = SECTION_PAGES[section];
            return {
              element: (
                <SectionGuard section={section}>
                  <Page />
                </SectionGuard>
              ),
            };
          },
        })),
        { path: '*', lazy: async () => ({ Component: (await load()).SettingsSectionNotFound }) },
      ],
    },
  ];
}
